/**
 * listaCampanas.js — la parte pura de la lista de campañas (rediseño, Fase 4). Sin React ni red.
 */

/** Los 6 estados del control segmentado, en este orden ('' = todas). */
export const ESTADOS_LISTA = [
  { value: '', label: 'Todas' },
  { value: 'Activa', label: 'Activas' },
  { value: 'Borrador', label: 'Borrador' },
  { value: 'Pausada', label: 'Pausadas' },
  { value: 'Completada', label: 'Completadas' },
  { value: 'Cancelada', label: 'Canceladas' },
];
const VALIDOS = new Set(ESTADOS_LISTA.map((e) => e.value).filter(Boolean));
export const MAX_BUSQUEDA = 100;

/**
 * Columnas por las que se ordena. Los nombres son los que acepta el servidor en ?orden= (lista cerrada
 * en GET /campanas); dir, 'asc' o 'desc'. Sin orden, el del servidor por defecto.
 */
export const ORDENES = ['titulo', 'periodo', 'progreso', 'estado'];
const DIRS = ['asc', 'desc'];

/**
 * Los filtros de la URL (?estado=Activa&q=texto&orden=progreso&dir=desc). Un estado que no existe se
 * ignora (todas), la búsqueda se recorta y se limita a MAX_BUSQUEDA caracteres, y un orden que no
 * existe se ignora (el del servidor); con orden válido y dir inválida o ausente, 'asc'.
 */
export function leerFiltrosLista(params) {
  const estado = params.get('estado') || '';
  const orden = params.get('orden') || '';
  const dir = params.get('dir') || '';
  const conOrden = ORDENES.includes(orden);
  return {
    estado: VALIDOS.has(estado) ? estado : '',
    q: (params.get('q') || '').trim().slice(0, MAX_BUSQUEDA),
    orden: conOrden ? orden : '',
    dir: conOrden ? (DIRS.includes(dir) ? dir : 'asc') : '',
  };
}

/**
 * Nuevos parámetros con esos filtros; los vacíos no se escriben y el resto de parámetros se respeta.
 * `orden`/`dir` solo se tocan si vienen en el objeto (cambiar el estado no quita el orden).
 */
export function paramsDeFiltrosLista(params, filtros) {
  const { estado, q } = filtros;
  const n = new URLSearchParams(params);
  if (estado && VALIDOS.has(estado)) n.set('estado', estado); else n.delete('estado');
  const t = (q || '').trim().slice(0, MAX_BUSQUEDA);
  if (t) n.set('q', t); else n.delete('q');
  if ('orden' in filtros) {
    if (ORDENES.includes(filtros.orden)) {
      n.set('orden', filtros.orden);
      n.set('dir', DIRS.includes(filtros.dir) ? filtros.dir : 'asc');
    } else {
      n.delete('orden');
      n.delete('dir');
    }
  }
  return n;
}

/**
 * Pulsar la cabecera de una columna: otra columna → ascendente; la misma, ascendente → descendente →
 * sin orden (vuelve al del servidor), como los 3 estados de la ordenación de la Table v2.5.
 */
export function siguienteOrden({ orden, dir }, columna) {
  if (orden !== columna) return { orden: columna, dir: 'asc' };
  if (dir === 'asc') return { orden: columna, dir: 'desc' };
  return { orden: '', dir: '' };
}

/** Los parámetros de orden para la API: solo si hay orden. */
export function paramsDeOrden({ orden, dir }) {
  return ORDENES.includes(orden) ? { orden, dir: DIRS.includes(dir) ? dir : 'asc' } : {};
}

const EDITABLES = ['Borrador', 'Activa', 'Pausada'];
const TERMINADAS = ['Completada', 'Cancelada'];

/**
 * Qué acciones se enseñan (la barrera es el servidor):
 *  - Nueva campaña, Editar / Continuar, Reactivar: intervenciones.campanas.manage.
 *  - Borrar (Borrador, Activa, Pausada) y Eliminar (Cancelada): intervenciones.campanas.delete,
 *    lo que pide DELETE /campanas/:id.
 *  - Ver resumen: todos (la lista ya pide campanas.view).
 *  - Lápiz desactivado en las terminadas, solo para quien podría editar.
 *  - Super Administrador, todo.
 */
export function permisosLista({ rol, tienePermiso, estado }) {
  const sa = rol === 'Super Administrador';
  const tiene = (p) => sa || (typeof tienePermiso === 'function' && !!tienePermiso(p));
  const gestionar = tiene('intervenciones.campanas.manage');
  const borrar = tiene('intervenciones.campanas.delete');
  const editable = EDITABLES.includes(estado);
  const terminada = TERMINADAS.includes(estado);
  return {
    nueva: gestionar,
    ver: true,
    editar: gestionar && editable,
    lapizDesactivado: gestionar && terminada,
    borrar: borrar && editable,
    reactivar: gestionar && estado === 'Cancelada',
    eliminar: borrar && estado === 'Cancelada',
  };
}

/** Clic en la fila: Borrador al asistente; el resto, al resumen. */
export function rutaFila(c) {
  return c.estado === 'Borrador'
    ? `/citas-tecnicas/campanas/${c.id}/wizard`
    : `/citas-tecnicas/campanas/${c.id}/resumen`;
}

/** El lápiz: Borrador al asistente, Activa o Pausada a Editar; las terminadas, ninguna. */
export function rutaEdicion(c) {
  if (c.estado === 'Borrador') return `/citas-tecnicas/campanas/${c.id}/wizard`;
  if (c.estado === 'Activa' || c.estado === 'Pausada') return `/citas-tecnicas/campanas/${c.id}/editar`;
  return null;
}
